import { UUID } from '../types';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { SimpleSettingsProperty } from '../properties/SimpleSettingsProperty';
import { Vector3 } from '../types/Vector3';
import { UpVectorMode, AlignmentMode, HoverDirection, RotationMode } from '../field/FieldTypes';

export interface LayouterProperties {
    [key: string]: any;
}

// Layout data types
export enum LayouterType {
    Grid = 'grid',
    Circle = 'circle',
    Spiral = 'spiral',
    Line = 'line',
    Track = 'track',
    Hexagonal = 'hexagonal',
    SphericalHexagonal = 'spherical_hexagonal',
    MapAnchor = 'map-anchor',
    Scatter = 'scatter',
    // Modern TestField Layouters
    ModernLine = 'modern-line',
    ModernCircle = 'modern-circle',
    ModernSpiral = 'modern-spiral',
    ModernLineAdaptive = 'modern-line-adaptive',
    ModernCircleAdaptive = 'modern-circle-adaptive',
    ModernSpiralAdaptive = 'modern-spiral-adaptive',
    ModernSphereGrid = 'modern-sphere-grid',
    ModernSphereHex = 'modern-sphere-hex',
    ModernPlaneGrid = 'modern-plane-grid',
    ModernSphereHexRings = 'modern-sphere-hex-rings',
    ModernPlaneHexRings = 'modern-plane-hex-rings',
    ModernSphereHexHoneycomb = 'modern-sphere-hex-honeycomb'
}

// Add field layout type enum
export enum FieldLayoutType {
    Line = 'line',
    Grid = 'grid',
    Circle = 'circle',
    Spiral = 'spiral',
    Hex = 'hex',
    HexSpherical = 'hex-spherical',
    GridSpherical = 'grid-spherical'
}

// NEW: Spacing distribution modes  
export enum SpacingMode {
    Fixed = 'fixed',        // Fixed spacing between objects, structure size varies (= margin mode)
    Distribute = 'distribute' // Fixed structure size, spacing varies uniformly (= distribute mode)
}

// Line direction types (for 1D line layouter)
export enum LineDirectionType {
    X = 'x',
    NegativeX = '-x',
    Y = 'y', 
    NegativeY = '-y',
    Z = 'z',
    NegativeZ = '-z'
}

// Spiral orientation modes
export enum SpiralOrientationMode {
    Fixed = 'fixed',           // All shapes have same orientation
    Band = 'band',             // Shapes follow spiral curve (perfect band)
    Step = 'step'              // Custom rotation step per index
}

// Spiral axis directions
export enum SpiralAxisType {
    Y = 'y',                   // Vertical spiral (default)
    X = 'x',                   // Horizontal spiral along X-axis
    Z = 'z',                   // Horizontal spiral along Z-axis
    NegativeY = '-y',          // Downward spiral
    NegativeX = '-x',          // Reverse horizontal spiral along X-axis
    NegativeZ = '-z'           // Reverse horizontal spiral along Z-axis
}



export class LayoutData {
    private static _instance: LayoutData | null = null;
    
    public static get Instance(): LayoutData {
        if (!LayoutData._instance) {
            LayoutData._instance = new LayoutData();
        }
        return LayoutData._instance;
    }
    
    public static setInstance(instance: LayoutData): void {
        LayoutData._instance = instance;
    }
    
    public id: UUID;
    
    // Layouter selection (instant, no animation needed)
    public readonly layouterProperty: SimpleSettingsProperty<LayouterType>;
    
    // ==================== GLOBAL SPACING MODE ====================
    public readonly spacingMode: SimpleSettingsProperty<SpacingMode>;
    
    // ==================== 1D LAYOUTER PARAMETERS ====================
    
    // LINE LAYOUTER (1D) - Only Fixed Spacing (uniform makes no sense for infinite lines)
    public readonly lineDirection: InterpolatedProperty<Vector3>;
    public readonly lineFixedSpacing: InterpolatedProperty<number>;
    public readonly lineCenterLayout: SimpleSettingsProperty<boolean>;
    public readonly lineJitter: InterpolatedProperty<number>;
    public readonly lineJitterSeed: SimpleSettingsProperty<number>;
    
    // CIRCLE LAYOUTER (1D) - Both Fixed and Uniform Spacing
    public readonly circleFixedSpacing: InterpolatedProperty<number>;  // Fixed: spacing between objects
    public readonly circleRadius: InterpolatedProperty<number>;        // Uniform: fixed radius
    public readonly circleOffsetY: InterpolatedProperty<number>;
    
    // SPIRAL LAYOUTER (1D) - Only Fixed Spacing (uniform makes no sense for infinite spirals)
    public readonly spiralFixedSpacing: InterpolatedProperty<number>;
    public readonly spiralTurns: InterpolatedProperty<number>;
    public readonly spiralRadius: InterpolatedProperty<number>;
    public readonly spiralHeight: InterpolatedProperty<number>;
    public readonly spiralAxis: SimpleSettingsProperty<SpiralAxisType>;
    public readonly spiralCenterLayout: SimpleSettingsProperty<boolean>;
    /** Radialer Linien-Versatz vom Layout-Zentroid (0 = durch die Objekte). */
    public readonly curveRadialOffset: InterpolatedProperty<number>;
    /** Vertikaler Linien-Versatz (positiv = Band über den Objekten). */
    public readonly curveVerticalOffset: InterpolatedProperty<number>;
    /** Honeycomb-Zellfuellgrad: 0.93 = nahtlose Kacheln, kleiner fuer 3D-Modelle mit Tiefe. */
    public readonly honeycombCellFill: InterpolatedProperty<number>;
    
    // ==================== 2D LAYOUTER PARAMETERS ====================
    
    // GRID LAYOUTER (2D Planar) - Both Fixed and Uniform Spacing
    public readonly gridFixedSpacing: InterpolatedProperty<number>;    // Fixed: spacing between grid points
    public readonly gridWidth: InterpolatedProperty<number>;           // Uniform: fixed surface width
    public readonly gridHeight: InterpolatedProperty<number>;          // Uniform: fixed surface height
    public readonly gridRows: InterpolatedProperty<number>;            // Manual override for grid structure
    public readonly gridCols: InterpolatedProperty<number>;            // Manual override for grid structure
    public readonly gridOffsetX: InterpolatedProperty<number>;
    public readonly gridOffsetY: InterpolatedProperty<number>;
    public readonly gridOffsetZ: InterpolatedProperty<number>;
    
    // HEXAGONAL LAYOUTER (2D Planar) - Both Fixed and Uniform Spacing
    public readonly hexFixedSpacing: InterpolatedProperty<number>;     // Fixed: spacing between hexagons
    public readonly hexWidth: InterpolatedProperty<number>;            // Uniform: fixed surface width
    public readonly hexHeight: InterpolatedProperty<number>;           // Uniform: fixed surface height
    public readonly hexOffsetZ: InterpolatedProperty<number>;
    
    // SPHERICAL LAYOUTER (2D Spherical) - Both Fixed and Uniform Spacing
    public readonly sphereFixedSpacing: InterpolatedProperty<number>;  // Fixed: spacing between objects on sphere
    public readonly sphereRadius: InterpolatedProperty<number>;        // Uniform: fixed sphere radius
    public readonly sphereSubdivisions: InterpolatedProperty<number>;

    // SCATTER LAYOUTER (3D Volume) - Pseudo-random placement in a bounding box
    public readonly scatterWidth: InterpolatedProperty<number>;
    public readonly scatterHeight: InterpolatedProperty<number>;
    public readonly scatterDepth: InterpolatedProperty<number>;
    public readonly scatterSeed: SimpleSettingsProperty<number>;

    // ==================== MODERN LAYOUTER PARAMETERS ====================
    
    // Modern 1D Layouters
    public readonly modernLineFixedSpacing: InterpolatedProperty<number>;
    public readonly modernCircleFixedSpacing: InterpolatedProperty<number>;
    public readonly modernCircleRadius: InterpolatedProperty<number>;
    public readonly modernSpiralFixedSpacing: InterpolatedProperty<number>;
    public readonly modernSpiralGrowth: InterpolatedProperty<number>;
    public readonly modernSpiralStartDistance: InterpolatedProperty<number>;
    public readonly modernSpiralTurns: InterpolatedProperty<number>;
    public readonly modernSpiralMaxRadius: InterpolatedProperty<number>;
    
    // Modern 2D/3D Layouters
    public readonly modernSphereGridFixedSpacing: InterpolatedProperty<number>;
    public readonly modernPlaneGridFixedSpacing: InterpolatedProperty<number>;
    public readonly modernSphereHexFixedSpacing: InterpolatedProperty<number>;
    public readonly modernSphereHexRingsFixedSpacing: InterpolatedProperty<number>;
    public readonly modernPlaneHexRingsFixedSpacing: InterpolatedProperty<number>;
    public readonly modernSphereHexHoneycombFixedSpacing: InterpolatedProperty<number>;
    
    // Modern Global Settings
    public readonly modernFlip180: SimpleSettingsProperty<boolean>;

    // ==================== GLOBAL ORIENTATION PARAMETERS ====================
    public readonly rotationStep: InterpolatedProperty<number>;
    public readonly alignmentMode: SimpleSettingsProperty<AlignmentMode>;
    public readonly upVectorMode: SimpleSettingsProperty<UpVectorMode>;
    
    // ==================== FIELD AWARENESS SYSTEM ====================
    public readonly fieldAwarenessEnabled: SimpleSettingsProperty<boolean>;
    public readonly fieldStrength: InterpolatedProperty<number>;
    public readonly fieldRadius: InterpolatedProperty<number>;
    public readonly fieldFalloff: InterpolatedProperty<number>;
    
    // ==================== SHAPE RESET CONTROL ====================
    public readonly resetShapesToDefault: SimpleSettingsProperty<boolean>;
    
    // ==================== EXTENDED FIELD AWARENESS PROPERTIES ====================
    public readonly fieldLayoutType: SimpleSettingsProperty<FieldLayoutType>;
    public readonly fieldHoverDirection: SimpleSettingsProperty<HoverDirection>;
    public readonly fieldHoverAmplitude: InterpolatedProperty<number>;
    public readonly fieldHoverFrequency: InterpolatedProperty<number>;
    public readonly fieldHoverPhaseShift: InterpolatedProperty<number>;
    public readonly fieldRotateAmplitude: InterpolatedProperty<number>;
    public readonly fieldRotateFrequency: InterpolatedProperty<number>;
    public readonly fieldRotatePhaseShift: InterpolatedProperty<number>;
    public readonly fieldRotationMode: SimpleSettingsProperty<RotationMode>;
    public readonly fieldScaleAmplitude: InterpolatedProperty<number>;
    public readonly fieldWaveSpeed: InterpolatedProperty<number>;
    public readonly fieldWindStrength: InterpolatedProperty<number>;
    public readonly fieldBreathingIntensity: InterpolatedProperty<number>;
    public readonly fieldOrganicVariation: InterpolatedProperty<number>;
    public readonly fieldDirection: InterpolatedProperty<Vector3>;
    public readonly fieldCellSize: InterpolatedProperty<number>;
    public readonly fieldCellSpacing: InterpolatedProperty<number>;
    public readonly fieldCustomUpVector: InterpolatedProperty<Vector3>;
    public readonly fieldCustomAlignmentVector: InterpolatedProperty<Vector3>;
    public readonly fieldAlignmentMode: SimpleSettingsProperty<AlignmentMode>;
    public readonly fieldUpVectorMode: SimpleSettingsProperty<UpVectorMode>;
    public readonly fieldHoverDirectionOverride: SimpleSettingsProperty<string>;
    

    
    // Custom layouter properties (non-animated for flexibility)
    public layouterProperties: LayouterProperties = {};
    
    // Backward compatibility getter
    public get layouter(): string {
        return this.layouterProperty.value || LayouterType.Grid;
    }
    
    public set layouter(value: string) {
        this.layouterProperty.value = value as LayouterType;
    }
    
    constructor(layouter: LayouterType = LayouterType.Grid) {
        this.id = crypto.randomUUID();
        
        // Create layouter selection property with automatic enum options
        this.layouterProperty = new SimpleSettingsProperty<LayouterType>('Layouter Type', null, layouter, 'Layout', Object.values(LayouterType));
        
        // Create spacing mode property
        this.spacingMode = new SimpleSettingsProperty<SpacingMode>('Spacing Mode', null, SpacingMode.Fixed, 'Layout', Object.values(SpacingMode));
        
        // Initialize all properties with InterpolatedProperty for SMOOTH ANIMATIONS! 🎬
        // Grid properties - only visible when 'grid' layouter is selected
        this.gridFixedSpacing = new InterpolatedProperty('Grid Spacing', null, 3.0, 0.8, 'Layout')
            .withBounds(0.1, 20, 0.1).visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridWidth = new InterpolatedProperty('Grid Width', null, 10.0, 1.0, 'Layout')
            .withBounds(0.1, 100, 0.1).visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridHeight = new InterpolatedProperty('Grid Height', null, 10.0, 1.0, 'Layout')
            .withBounds(0.1, 100, 0.1).visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridRows = new InterpolatedProperty('Grid Rows', null, 2, 0.6, 'Layout')
            .withBounds(1, 20, 1).visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridCols = new InterpolatedProperty('Grid Columns', null, 3, 0.6, 'Layout')
            .withBounds(1, 20, 1).visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridOffsetX = new InterpolatedProperty('Grid Offset X', null, 0, 0.5, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridOffsetY = new InterpolatedProperty('Grid Offset Y', null, 0, 0.5, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Grid);
        this.gridOffsetZ = new InterpolatedProperty('Grid Offset Z', null, 0, 0.5, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Grid);
        
        // Circle properties - only visible when 'circle' layouter is selected
        this.circleFixedSpacing = new InterpolatedProperty('Circle Spacing', null, 1.0, 0.8, 'Layout')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.Circle);
        this.circleRadius = new InterpolatedProperty('Circle Radius', null, 5.0, 1.0, 'Layout')
            .withBounds(0.1, 50, 0.1).visibleWhen('Layouter Type', LayouterType.Circle);
        this.circleOffsetY = new InterpolatedProperty('Circle Offset Y', null, 0, 0.5, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Circle);
        
        // Spiral properties - only visible when 'spiral' layouter is selected
        this.spiralFixedSpacing = new InterpolatedProperty('Spiral Spacing', null, 1.0, 0.8, 'Layout')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.Spiral);
        this.spiralTurns = new InterpolatedProperty('Spiral Turns', null, 2.0, 0.8, 'Layout')
            .withBounds(0, 20, 0.1).visibleWhen('Layouter Type', LayouterType.Spiral);
        this.spiralRadius = new InterpolatedProperty('Spiral Radius', null, 3.0, 0.8, 'Layout')
            .withBounds(0.1, 50, 0.1).visibleWhen('Layouter Type', LayouterType.Spiral);
        this.spiralHeight = new InterpolatedProperty('Spiral Height', null, 10.0, 1.2, 'Layout')
            .withBounds(0.1, 100, 0.1).visibleWhen('Layouter Type', LayouterType.Spiral);
        this.spiralAxis = new SimpleSettingsProperty<SpiralAxisType>('Spiral Axis', null, SpiralAxisType.Y, 'Layout', Object.values(SpiralAxisType))
            .visibleWhen('Layouter Type', LayouterType.Spiral);
        this.spiralCenterLayout = new SimpleSettingsProperty<boolean>('Center Layout', null, false, 'Layout', [true, false])
            .visibleWhen('Layouter Type', LayouterType.Spiral);

        // Radialer Versatz der Präsentationslinie gegenüber den Objektpositionen.
        // 0 = Linie läuft durch die Objekte (bisheriges Verhalten). Positiv schiebt
        // sie horizontal vom Layout-Zentroid weg (Spirale: äußeres Band hinter den
        // Karten), negativ nach innen. Layouter-unabhängig — wirkt auf die Punkte,
        // nicht auf das Layout selbst.
        this.curveRadialOffset = new InterpolatedProperty('Curve Radial Offset', null, 0, 0.8, 'Layout')
            .withBounds(-10, 10, 0.1);
        this.curveVerticalOffset = new InterpolatedProperty('Curve Vertical Offset', null, 0, 0.8, 'Layout')
            .withBounds(-10, 10, 0.1);
        this.honeycombCellFill = new InterpolatedProperty('Honeycomb Cell Fill', null, 0.93, 0.8, 'Modern Layouts')
            .withBounds(0.1, 1.2, 0.01);
        
        // Line properties - only visible when 'line' layouter is selected
        this.lineDirection = new InterpolatedProperty<Vector3>('Line Direction', null, new Vector3(0, 0, 1), 1.0, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Line);
        this.lineFixedSpacing = new InterpolatedProperty('Line Spacing', null, 3.0, 0.6, 'Layout')
            .withBounds(0.1, 20, 0.1).visibleWhen('Layouter Type', LayouterType.Line);
        this.lineCenterLayout = new SimpleSettingsProperty<boolean>('Center Layout', null, false, 'Layout', [true, false])
            .visibleWhen('Layouter Type', LayouterType.Line);
        this.lineJitter = new InterpolatedProperty('Line Jitter', null, 0, 0.5, 'Layout')
            .withBounds(0, 10, 0.1).visibleWhen('Layouter Type', LayouterType.Line);
        this.lineJitterSeed = new SimpleSettingsProperty<number>('Jitter Seed', null, 42, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Line);

                 // Hexagonal properties - only visible when 'hexagonal' layouter is selected
         this.hexFixedSpacing = new InterpolatedProperty('Hex Spacing (0=perfect fit)', null, 0.01, 0.8, 'Layout')
             .withBounds(0.0, 10, 0.1).visibleWhen('Layouter Type', LayouterType.Hexagonal);
        this.hexWidth = new InterpolatedProperty('Hex Width', null, 10.0, 1.0, 'Layout')
            .withBounds(0.1, 100, 0.1).visibleWhen('Layouter Type', LayouterType.Hexagonal);
        this.hexHeight = new InterpolatedProperty('Hex Height', null, 10.0, 1.0, 'Layout')
            .withBounds(0.1, 100, 0.1).visibleWhen('Layouter Type', LayouterType.Hexagonal);
        this.hexOffsetZ = new InterpolatedProperty('Hex Offset Z', null, 0, 0.5, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Hexagonal);
        
        // Spherical properties - only visible when 'spherical' layouter is selected
        this.sphereFixedSpacing = new InterpolatedProperty('Sphere Spacing', null, 1.0, 0.8, 'Layout')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.SphericalHexagonal);
        this.sphereRadius = new InterpolatedProperty('Sphere Radius', null, 8.0, 1.2, 'Layout')
            .withBounds(1.0, 100, 0.1).visibleWhen('Layouter Type', LayouterType.SphericalHexagonal);
        this.sphereSubdivisions = new InterpolatedProperty('Sphere Subdivisions', null, 2, 0.6, 'Layout')
            .withBounds(1, 10, 1).visibleWhen('Layouter Type', LayouterType.SphericalHexagonal);

        // Scatter properties - only visible when 'scatter' layouter is selected
        this.scatterWidth = new InterpolatedProperty('Scatter Width', null, 10, 0.5, 'Layout')
            .withBounds(1, 100, 0.5).visibleWhen('Layouter Type', LayouterType.Scatter);
        this.scatterHeight = new InterpolatedProperty('Scatter Height', null, 6, 0.5, 'Layout')
            .withBounds(1, 100, 0.5).visibleWhen('Layouter Type', LayouterType.Scatter);
        this.scatterDepth = new InterpolatedProperty('Scatter Depth', null, 8, 0.5, 'Layout')
            .withBounds(1, 100, 0.5).visibleWhen('Layouter Type', LayouterType.Scatter);
        this.scatterSeed = new SimpleSettingsProperty<number>('Scatter Seed', null, 42, 'Layout')
            .visibleWhen('Layouter Type', LayouterType.Scatter);

        // Global rotation (always visible)
        this.rotationStep = new InterpolatedProperty('Rotation Step', null, 0, 0.4, 'Layout')
            .withBounds(-360, 360, 1);
        
        // Alignment Mode (visible for all layouters)
        this.alignmentMode = new SimpleSettingsProperty<AlignmentMode>('Alignment Mode', AlignmentMode.Aligned, AlignmentMode.Aligned, 'Layout', Object.values(AlignmentMode));
        
        // Up Vector Mode (visible for all layouters) - NEW!
        this.upVectorMode = new SimpleSettingsProperty<UpVectorMode>('Up Vector Mode', UpVectorMode.WorldUp, UpVectorMode.WorldUp, 'Layout', Object.values(UpVectorMode));
        
        // ==================== MODERN LAYOUTER PROPERTIES ====================
        
        // Modern 1D Layouters
        this.modernLineFixedSpacing = new InterpolatedProperty('Modern Line Spacing', null, 3.0, 0.6, 'Modern Layouts')
            .withBounds(0.1, 20, 0.1).visibleWhen('Layouter Type', LayouterType.ModernLine);
        
        this.modernCircleFixedSpacing = new InterpolatedProperty('Modern Circle Spacing', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernCircle);
        this.modernCircleRadius = new InterpolatedProperty('Modern Circle Radius', null, 5.0, 1.0, 'Modern Layouts')
            .withBounds(0.1, 50, 0.1).visibleWhen('Layouter Type', LayouterType.ModernCircle);
        
        this.modernSpiralFixedSpacing = new InterpolatedProperty('Modern Spiral Spacing', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSpiral);
        this.modernSpiralGrowth = new InterpolatedProperty('Modern Spiral Growth', null, 2.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSpiral);
        this.modernSpiralStartDistance = new InterpolatedProperty('Modern Spiral Start Distance', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSpiral);
        this.modernSpiralTurns = new InterpolatedProperty('Modern Spiral Turns', null, 3.0, 0.8, 'Modern Layouts')
            .withBounds(1, 20, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSpiral);
        this.modernSpiralMaxRadius = new InterpolatedProperty('Modern Spiral Max Radius', null, 10.0, 1.0, 'Modern Layouts')
            .withBounds(1, 50, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSpiral);
        
        // Modern Adaptive Layouters (shared properties with different visibility)
        this.modernLineFixedSpacing
            .visibleWhen('Layouter Type', LayouterType.ModernLineAdaptive);
        this.modernCircleFixedSpacing
            .visibleWhen('Layouter Type', LayouterType.ModernCircleAdaptive);
        this.modernCircleRadius
            .visibleWhen('Layouter Type', LayouterType.ModernCircleAdaptive);
        this.modernSpiralFixedSpacing
            .visibleWhen('Layouter Type', LayouterType.ModernSpiralAdaptive);
        this.modernSpiralGrowth
            .visibleWhen('Layouter Type', LayouterType.ModernSpiralAdaptive);
        this.modernSpiralStartDistance
            .visibleWhen('Layouter Type', LayouterType.ModernSpiralAdaptive);
        this.modernSpiralTurns
            .visibleWhen('Layouter Type', LayouterType.ModernSpiralAdaptive);
        this.modernSpiralMaxRadius
            .visibleWhen('Layouter Type', LayouterType.ModernSpiralAdaptive);
        
        // Modern 2D/3D Layouters
        this.modernSphereGridFixedSpacing = new InterpolatedProperty('Modern Sphere Grid Spacing', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSphereGrid);
        this.modernPlaneGridFixedSpacing = new InterpolatedProperty('Modern Plane Grid Spacing', null, 2.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernPlaneGrid);
        this.modernSphereHexFixedSpacing = new InterpolatedProperty('Modern Sphere Hex Spacing', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSphereHex);
        this.modernSphereHexRingsFixedSpacing = new InterpolatedProperty('Modern Sphere Hex Rings Spacing', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSphereHexRings);
        this.modernPlaneHexRingsFixedSpacing = new InterpolatedProperty('Modern Plane Hex Rings Spacing', null, 2.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernPlaneHexRings);
        this.modernSphereHexHoneycombFixedSpacing = new InterpolatedProperty('Modern Sphere Hex Honeycomb Spacing', null, 1.0, 0.8, 'Modern Layouts')
            .withBounds(0.1, 10, 0.1).visibleWhen('Layouter Type', LayouterType.ModernSphereHexHoneycomb);
        
        // Modern Global Settings
        this.modernFlip180 = new SimpleSettingsProperty<boolean>('Modern Flip 180°', false, false, 'Modern Layouts', [true, false]);
        
        // Field Awareness properties
        this.fieldAwarenessEnabled = new SimpleSettingsProperty<boolean>('Field Awareness Enabled', false, false, 'Field Awareness');
        
        // Shape Reset Control property
        this.resetShapesToDefault = new SimpleSettingsProperty<boolean>('Reset Shapes to Default', false, false, 'Layout');
        
        this.fieldStrength = new InterpolatedProperty('Field Strength', null, 1, 5, 'Field Awareness')
            .withBounds(0, 10, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldRadius = new InterpolatedProperty('Field Radius', null, 10, 5, 'Field Awareness')
            .withBounds(1, 100, 0.1).visibleWhen('Field Awareness Enabled', true);
        this.fieldFalloff = new InterpolatedProperty('Field Falloff', null, 1, 5, 'Field Awareness')
            .withBounds(0.1, 10, 0.01).visibleWhen('Field Awareness Enabled', true);
            
        // Extended Field Awareness properties
        this.fieldLayoutType = new SimpleSettingsProperty<FieldLayoutType>('Field Layout Type', FieldLayoutType.Grid, FieldLayoutType.Grid, 'Field Awareness', Object.values(FieldLayoutType))
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldHoverDirection = new SimpleSettingsProperty<HoverDirection>('Field Hover Direction', HoverDirection.Transversal, HoverDirection.Transversal, 'Field Awareness', Object.values(HoverDirection))
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldHoverAmplitude = new InterpolatedProperty('Field Hover Amplitude', null, 1.5, 0.5, 'Field Awareness')
            .withBounds(0, 10, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldHoverFrequency = new InterpolatedProperty('Field Hover Frequency', null, 0.001, 0.5, 'Field Awareness')
            .withBounds(0, 0.1, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldHoverPhaseShift = new InterpolatedProperty('Field Hover Phase Shift', null, 0.0, 0.5, 'Field Awareness')
            .withBounds(0, 6.28, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldRotateAmplitude = new InterpolatedProperty('Field Rotate Amplitude', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 3, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldRotateFrequency = new InterpolatedProperty('Field Rotate Frequency', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 5, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldRotatePhaseShift = new InterpolatedProperty('Field Rotate Phase Shift', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 6.28, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldRotationMode = new SimpleSettingsProperty<RotationMode>('Field Rotation Mode', RotationMode.Oscillating, RotationMode.Oscillating, 'Field Awareness', Object.values(RotationMode))
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldScaleAmplitude = new InterpolatedProperty('Field Scale Amplitude', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 2, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldWaveSpeed = new InterpolatedProperty('Field Wave Speed', null, 1, 0.5, 'Field Awareness')
            .withBounds(0, 10, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldWindStrength = new InterpolatedProperty('Field Wind Strength', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 2, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldBreathingIntensity = new InterpolatedProperty('Field Breathing Intensity', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 5, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldOrganicVariation = new InterpolatedProperty('Field Organic Variation', null, 0, 0.5, 'Field Awareness')
            .withBounds(0, 2, 0.01).visibleWhen('Field Awareness Enabled', true);
        this.fieldDirection = new InterpolatedProperty('Field Direction', null, new Vector3(0, 0, 1), 1.0, 'Field Awareness')
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldCellSize = new InterpolatedProperty('Field Cell Size', null, 2, 0.5, 'Field Awareness')
            .withBounds(0.001, 20, 0.1).visibleWhen('Field Awareness Enabled', true);
        this.fieldCellSpacing = new InterpolatedProperty('Field Cell Spacing', null, 0, 0.5, 'Field Awareness')
            .withBounds(-5, 5, 0.1).visibleWhen('Field Awareness Enabled', true);
        this.fieldCustomUpVector = new InterpolatedProperty('Field Custom Up Vector', null, new Vector3(0, 1, 0), 1.0, 'Field Awareness')
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldCustomAlignmentVector = new InterpolatedProperty('Field Custom Alignment Vector', null, new Vector3(0, 0, 1), 1.0, 'Field Awareness')
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldAlignmentMode = new SimpleSettingsProperty<AlignmentMode>('Field Alignment Mode', AlignmentMode.Aligned, AlignmentMode.Aligned, 'Field Awareness', Object.values(AlignmentMode))
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldUpVectorMode = new SimpleSettingsProperty<UpVectorMode>('Field Up Vector Mode', UpVectorMode.WorldUp, UpVectorMode.WorldUp, 'Field Awareness', Object.values(UpVectorMode))
            .visibleWhen('Field Awareness Enabled', true);
        this.fieldHoverDirectionOverride = new SimpleSettingsProperty<string>('Field Hover Direction Override', 'none', 'none', 'Field Awareness', ['none', 'vertical', 'horizontal'])
            .visibleWhen('Field Awareness Enabled', true);
            
        // ==================== MODERN LAYOUTER PROPERTIES (TestField Integration) ====================
        
        // Modern Line Layouter (Standard + Adaptive)
        this.modernLineFixedSpacing = new InterpolatedProperty('Line Spacing', null, 3.0, 0.8, 'Modern Layouters')
            .withBounds(0.1, 10.0, 0.1);
        
        // Modern Circle Layouter (Standard + Adaptive)
        this.modernCircleFixedSpacing = new InterpolatedProperty('Circle Spacing', null, 1.0, 0.8, 'Modern Layouters')
            .withBounds(0.1, 5.0, 0.1);
        this.modernCircleRadius = new InterpolatedProperty('Radius (Circle/Sphere)', null, 5.0, 0.8, 'Modern Layouters')
            .withBounds(1.0, 15.0, 0.2);
        
        // Modern Spiral Layouter (Standard + Adaptive)  
        this.modernSpiralFixedSpacing = new InterpolatedProperty('Spiral Spacing', null, 1.0, 0.8, 'Modern Layouters')
            .withBounds(0.1, 5.0, 0.1);
        this.modernSpiralGrowth = new InterpolatedProperty('Spiral Growth Rate', null, 2.0, 0.8, 'Modern Layouters')
            .withBounds(0.5, 4.0, 0.1);
        this.modernSpiralStartDistance = new InterpolatedProperty('Spiral Start Distance', null, 0.0, 0.8, 'Modern Layouters')
            .withBounds(0.0, 1.0, 0.05);
        this.modernSpiralTurns = new InterpolatedProperty('Spiral Turns', null, 2.0, 0.8, 'Modern Layouters')
            .withBounds(1.0, 8.0, 0.5);
        this.modernSpiralMaxRadius = new InterpolatedProperty('Spiral Max Radius', null, 3.0, 0.8, 'Modern Layouters')
            .withBounds(1.0, 6.0, 0.2);
        
        // Modern Surface Layouters (2D/3D)
        this.modernSphereGridFixedSpacing = new InterpolatedProperty('Sphere Grid Spacing', null, 1.5, 0.8, 'Modern Layouters')
            .withBounds(0.5, 4.0, 0.1);
        this.modernPlaneGridFixedSpacing = new InterpolatedProperty('Plane Grid Spacing', null, 2.0, 0.8, 'Modern Layouters')
            .withBounds(0.5, 5.0, 0.1);
        this.modernSphereHexFixedSpacing = new InterpolatedProperty('Sphere Hex Spacing', null, 1.0, 0.8, 'Modern Layouters')
            .withBounds(0.3, 3.0, 0.1);
        this.modernSphereHexRingsFixedSpacing = new InterpolatedProperty('Sphere Hex Rings Spacing', null, 1.2, 0.8, 'Modern Layouters')
            .withBounds(0.5, 3.0, 0.1);
        this.modernPlaneHexRingsFixedSpacing = new InterpolatedProperty('Plane Hex Rings Spacing', null, 1.8, 0.8, 'Modern Layouters')
            .withBounds(0.8, 4.0, 0.1);
        this.modernSphereHexHoneycombFixedSpacing = new InterpolatedProperty('Sphere Honeycomb Spacing', null, 1.0, 0.8, 'Modern Layouters')
            .withBounds(0.3, 3.0, 0.1);
        
        // Common Modern Layouter Properties
        this.modernFlip180 = new SimpleSettingsProperty<boolean>('180° Flip', false, false, 'Modern Layouters', [true, false]);
    }
    
    // Convenience methods for bulk updates with SMOOTH ANIMATIONS! 
    updateGridParameters(props: {
        spacing?: number;
        rows?: number;
        cols?: number;
        offsetX?: number;
        offsetY?: number;
        offsetZ?: number;
    }): void {
        if (props.spacing !== undefined) this.gridFixedSpacing.value = props.spacing;
        if (props.rows !== undefined) this.gridRows.value = props.rows;
        if (props.cols !== undefined) this.gridCols.value = props.cols;
        if (props.offsetX !== undefined) this.gridOffsetX.value = props.offsetX;
        if (props.offsetY !== undefined) this.gridOffsetY.value = props.offsetY;
        if (props.offsetZ !== undefined) this.gridOffsetZ.value = props.offsetZ;
    }
    
    updateCircleParameters(props: {
        radius?: number;
        offsetY?: number;
    }): void {
        if (props.radius !== undefined) this.circleRadius.value = props.radius;
        if (props.offsetY !== undefined) this.circleOffsetY.value = props.offsetY;
    }
    
    updateSpiralParameters(props: {
        radius?: number;
        height?: number;
        turns?: number;
        orientationMode?: SpiralOrientationMode;
        axis?: SpiralAxisType;
        spacing?: number;
        centerLayout?: boolean;
    }): void {
                 if (props.radius !== undefined) this.spiralRadius.value = props.radius;
         if (props.height !== undefined) this.spiralHeight.value = props.height;
         if (props.turns !== undefined) this.spiralTurns.value = props.turns;
         if (props.axis !== undefined) this.spiralAxis.value = props.axis;
         if (props.spacing !== undefined) this.spiralFixedSpacing.value = props.spacing;
         if (props.centerLayout !== undefined) this.spiralCenterLayout.value = props.centerLayout;
    }
    
    updateLineParameters(props: {
        spacing?: number;
        direction?: Vector3;
        rotationStep?: number;
        centerLayout?: boolean;
    }): void {
        if (props.spacing !== undefined) this.lineFixedSpacing.value = props.spacing;
        if (props.direction !== undefined) this.lineDirection.value = props.direction;
        if (props.rotationStep !== undefined) this.rotationStep.value = props.rotationStep;
        if (props.centerLayout !== undefined) this.lineCenterLayout.value = props.centerLayout;
    }
    
    updateHexagonalParameters(props: {
        spacing?: number;
        offsetZ?: number;
    }): void {
        if (props.spacing !== undefined) this.hexFixedSpacing.value = props.spacing;
        if (props.offsetZ !== undefined) this.hexOffsetZ.value = props.offsetZ;
    }
    
    updateSphericalParameters(props: {
        radius?: number;
        subdivisions?: number;
    }): void {
        if (props.radius !== undefined) this.sphereRadius.value = props.radius;
        if (props.subdivisions !== undefined) this.sphereSubdivisions.value = props.subdivisions;
    }
    
    // Legacy methods (for backward compatibility)
    setLayouter(name: LayouterType): LayoutData {
        this.layouterProperty.value = name;
        return this;
    }
    
    setGridParameters(spacing: number, rows: number, cols: number): LayoutData {
        this.updateGridParameters({ spacing, rows, cols });
        return this;
    }
    
    setCircleParameters(radius: number, offsetY: number = 0): LayoutData {
        this.updateCircleParameters({ radius, offsetY });
        return this;
    }
    
    setSpiralParameters(radius: number, height: number, turns: number, orientationMode: SpiralOrientationMode = SpiralOrientationMode.Fixed, axis: SpiralAxisType = SpiralAxisType.Y, spacing: number = 1.0): LayoutData {
        this.updateSpiralParameters({ radius, height, turns, orientationMode, axis, spacing });
        return this;
    }
    
    setLineParameters(spacing: number, direction: Vector3 = new Vector3(0, 0, 1), rotationStep: number = 0): LayoutData {
        this.updateLineParameters({ spacing, direction, rotationStep });
        return this;
    }
    
    setRotationStep(step: number): LayoutData {
        this.rotationStep.value = step;
        return this;
    }
    
    // Set custom layouter property
    setLayouterProperty(key: string, value: any): LayoutData {
        this.layouterProperties[key] = value;
        return this;
    }
    
    // Get custom layouter property
    getLayouterProperty(key: string, defaultValue: any = undefined): any {
        return this.layouterProperties[key] ?? defaultValue;
    }
    
    // Get current values (for serialization or immediate access)
            getCurrentValues(): any {
        return {
            id: this.id,
            layouter: this.layouterProperty.value,
            spacingMode: this.spacingMode.value,
            lineDirection: this.lineDirection.value,
            lineFixedSpacing: this.lineFixedSpacing.value,
            lineCenterLayout: this.lineCenterLayout.value,
            circleFixedSpacing: this.circleFixedSpacing.value,
            circleRadius: this.circleRadius.value,
            circleOffsetY: this.circleOffsetY.value,
            spiralFixedSpacing: this.spiralFixedSpacing.value,
            spiralTurns: this.spiralTurns.value,
            spiralRadius: this.spiralRadius.value,
            spiralHeight: this.spiralHeight.value,
            spiralAxis: this.spiralAxis.value,
            spiralCenterLayout: this.spiralCenterLayout.value,
            gridFixedSpacing: this.gridFixedSpacing.value,
            gridWidth: this.gridWidth.value,
            gridHeight: this.gridHeight.value,
            gridRows: this.gridRows.value,
            gridCols: this.gridCols.value,
            gridOffsetX: this.gridOffsetX.value,
            gridOffsetY: this.gridOffsetY.value,
            gridOffsetZ: this.gridOffsetZ.value,
            hexFixedSpacing: this.hexFixedSpacing.value,
            hexWidth: this.hexWidth.value,
            hexHeight: this.hexHeight.value,
            hexOffsetZ: this.hexOffsetZ.value,
            sphereFixedSpacing: this.sphereFixedSpacing.value,
            sphereRadius: this.sphereRadius.value,
            sphereSubdivisions: this.sphereSubdivisions.value,
            scatterWidth: this.scatterWidth.value,
            scatterHeight: this.scatterHeight.value,
            scatterDepth: this.scatterDepth.value,
            scatterSeed: this.scatterSeed.value,
            rotationStep: this.rotationStep.value,
            alignmentMode: this.alignmentMode.value,
            upVectorMode: this.upVectorMode.value,
            fieldAwarenessEnabled: this.fieldAwarenessEnabled.value,
            resetShapesToDefault: this.resetShapesToDefault.value,
            fieldStrength: this.fieldStrength.value,
            fieldRadius: this.fieldRadius.value,
            fieldFalloff: this.fieldFalloff.value,
            // Extended field properties
            fieldLayoutType: this.fieldLayoutType.value,
            fieldHoverDirection: this.fieldHoverDirection.value,
            fieldHoverAmplitude: this.fieldHoverAmplitude.value,
            fieldHoverFrequency: this.fieldHoverFrequency.value,
            fieldHoverPhaseShift: this.fieldHoverPhaseShift.value,
            fieldRotateAmplitude: this.fieldRotateAmplitude.value,
            fieldRotateFrequency: this.fieldRotateFrequency.value,
            fieldRotatePhaseShift: this.fieldRotatePhaseShift.value,
            fieldRotationMode: this.fieldRotationMode.value,
            fieldScaleAmplitude: this.fieldScaleAmplitude.value,
            fieldWaveSpeed: this.fieldWaveSpeed.value,
            fieldWindStrength: this.fieldWindStrength.value,
            fieldBreathingIntensity: this.fieldBreathingIntensity.value,
            fieldOrganicVariation: this.fieldOrganicVariation.value,
            fieldDirection: this.fieldDirection.value,
            fieldCellSize: this.fieldCellSize.value,
            fieldCellSpacing: this.fieldCellSpacing.value,
            fieldCustomUpVector: this.fieldCustomUpVector.value,
            fieldCustomAlignmentVector: this.fieldCustomAlignmentVector.value,
            fieldAlignmentMode: this.fieldAlignmentMode.value,
            fieldUpVectorMode: this.fieldUpVectorMode.value,
            fieldHoverDirectionOverride: this.fieldHoverDirectionOverride.value,
            // Modern Layouter Properties  
            modernLineFixedSpacing: this.modernLineFixedSpacing.value,
            modernCircleFixedSpacing: this.modernCircleFixedSpacing.value,
            modernCircleRadius: this.modernCircleRadius.value,
            modernSpiralFixedSpacing: this.modernSpiralFixedSpacing.value,
            modernSpiralGrowth: this.modernSpiralGrowth.value,
            modernSpiralStartDistance: this.modernSpiralStartDistance.value,
            modernSpiralTurns: this.modernSpiralTurns.value,
            modernSpiralMaxRadius: this.modernSpiralMaxRadius.value,
            // Modern Surface Layouters
            modernSphereGridFixedSpacing: this.modernSphereGridFixedSpacing.value,
            modernPlaneGridFixedSpacing: this.modernPlaneGridFixedSpacing.value,
            modernSphereHexFixedSpacing: this.modernSphereHexFixedSpacing.value,
            modernSphereHexRingsFixedSpacing: this.modernSphereHexRingsFixedSpacing.value,
            modernPlaneHexRingsFixedSpacing: this.modernPlaneHexRingsFixedSpacing.value,
            modernSphereHexHoneycombFixedSpacing: this.modernSphereHexHoneycombFixedSpacing.value,
            modernFlip180: this.modernFlip180.value,
            layouterProperties: { ...this.layouterProperties }
        };
    }
    
    // Set all values immediately (no animation)
    setImmediate(data: any): void {
        if (data.id) this.id = data.id;
        if (data.layouter) this.layouterProperty.setImmediate(data.layouter);
        if (data.spacingMode !== undefined) this.spacingMode.setImmediate(data.spacingMode);
        if (data.lineDirection !== undefined) this.lineDirection.setImmediate(data.lineDirection);
        if (data.lineFixedSpacing !== undefined) this.lineFixedSpacing.setImmediate(data.lineFixedSpacing);
        if (data.lineCenterLayout !== undefined) this.lineCenterLayout.setImmediate(data.lineCenterLayout);
        if (data.circleFixedSpacing !== undefined) this.circleFixedSpacing.setImmediate(data.circleFixedSpacing);
        if (data.circleRadius !== undefined) this.circleRadius.setImmediate(data.circleRadius);
        if (data.circleOffsetY !== undefined) this.circleOffsetY.setImmediate(data.circleOffsetY);
        if (data.spiralFixedSpacing !== undefined) this.spiralFixedSpacing.setImmediate(data.spiralFixedSpacing);
        if (data.spiralTurns !== undefined) this.spiralTurns.setImmediate(data.spiralTurns);
        if (data.spiralRadius !== undefined) this.spiralRadius.setImmediate(data.spiralRadius);
        if (data.spiralHeight !== undefined) this.spiralHeight.setImmediate(data.spiralHeight);
        if (data.spiralAxis !== undefined) this.spiralAxis.setImmediate(data.spiralAxis);
        if (data.spiralCenterLayout !== undefined) this.spiralCenterLayout.setImmediate(data.spiralCenterLayout);
        if (data.gridFixedSpacing !== undefined) this.gridFixedSpacing.setImmediate(data.gridFixedSpacing);
        if (data.gridWidth !== undefined) this.gridWidth.setImmediate(data.gridWidth);
        if (data.gridHeight !== undefined) this.gridHeight.setImmediate(data.gridHeight);
        if (data.gridRows !== undefined) this.gridRows.setImmediate(data.gridRows);
        if (data.gridCols !== undefined) this.gridCols.setImmediate(data.gridCols);
        if (data.gridOffsetX !== undefined) this.gridOffsetX.setImmediate(data.gridOffsetX);
        if (data.gridOffsetY !== undefined) this.gridOffsetY.setImmediate(data.gridOffsetY);
        if (data.gridOffsetZ !== undefined) this.gridOffsetZ.setImmediate(data.gridOffsetZ);
        if (data.hexFixedSpacing !== undefined) this.hexFixedSpacing.setImmediate(data.hexFixedSpacing);
        if (data.hexWidth !== undefined) this.hexWidth.setImmediate(data.hexWidth);
        if (data.hexHeight !== undefined) this.hexHeight.setImmediate(data.hexHeight);
        if (data.hexOffsetZ !== undefined) this.hexOffsetZ.setImmediate(data.hexOffsetZ);
        if (data.sphereFixedSpacing !== undefined) this.sphereFixedSpacing.setImmediate(data.sphereFixedSpacing);
        if (data.sphereRadius !== undefined) this.sphereRadius.setImmediate(data.sphereRadius);
        if (data.sphereSubdivisions !== undefined) this.sphereSubdivisions.setImmediate(data.sphereSubdivisions);
        if (data.scatterWidth !== undefined) this.scatterWidth.setImmediate(data.scatterWidth);
        if (data.scatterHeight !== undefined) this.scatterHeight.setImmediate(data.scatterHeight);
        if (data.scatterDepth !== undefined) this.scatterDepth.setImmediate(data.scatterDepth);
        if (data.scatterSeed !== undefined) this.scatterSeed.setImmediate(data.scatterSeed);
        if (data.rotationStep !== undefined) this.rotationStep.setImmediate(data.rotationStep);
        if (data.alignmentMode !== undefined) this.alignmentMode.setImmediate(data.alignmentMode);
        if (data.upVectorMode !== undefined) this.upVectorMode.setImmediate(data.upVectorMode);
        
        // Field Awareness properties
        if (data.fieldAwarenessEnabled !== undefined) this.fieldAwarenessEnabled.setImmediate(data.fieldAwarenessEnabled);
        if (data.resetShapesToDefault !== undefined) this.resetShapesToDefault.setImmediate(data.resetShapesToDefault);
        if (data.fieldStrength !== undefined) this.fieldStrength.setImmediate(data.fieldStrength);
        if (data.fieldRadius !== undefined) this.fieldRadius.setImmediate(data.fieldRadius);
        if (data.fieldFalloff !== undefined) this.fieldFalloff.setImmediate(data.fieldFalloff);
        
        // Extended Field Awareness properties
        if (data.fieldLayoutType !== undefined) this.fieldLayoutType.setImmediate(data.fieldLayoutType);
        if (data.fieldHoverDirection !== undefined) this.fieldHoverDirection.setImmediate(data.fieldHoverDirection);
        if (data.fieldHoverAmplitude !== undefined) this.fieldHoverAmplitude.setImmediate(data.fieldHoverAmplitude);
        if (data.fieldHoverFrequency !== undefined) this.fieldHoverFrequency.setImmediate(data.fieldHoverFrequency);
        if (data.fieldHoverPhaseShift !== undefined) this.fieldHoverPhaseShift.setImmediate(data.fieldHoverPhaseShift);
        if (data.fieldRotateAmplitude !== undefined) this.fieldRotateAmplitude.setImmediate(data.fieldRotateAmplitude);
        if (data.fieldRotateFrequency !== undefined) this.fieldRotateFrequency.setImmediate(data.fieldRotateFrequency);
        if (data.fieldRotatePhaseShift !== undefined) this.fieldRotatePhaseShift.setImmediate(data.fieldRotatePhaseShift);
        if (data.fieldRotationMode !== undefined) this.fieldRotationMode.setImmediate(data.fieldRotationMode);
        if (data.fieldScaleAmplitude !== undefined) this.fieldScaleAmplitude.setImmediate(data.fieldScaleAmplitude);
        if (data.fieldWaveSpeed !== undefined) this.fieldWaveSpeed.setImmediate(data.fieldWaveSpeed);
        if (data.fieldWindStrength !== undefined) this.fieldWindStrength.setImmediate(data.fieldWindStrength);
        if (data.fieldBreathingIntensity !== undefined) this.fieldBreathingIntensity.setImmediate(data.fieldBreathingIntensity);
        if (data.fieldOrganicVariation !== undefined) this.fieldOrganicVariation.setImmediate(data.fieldOrganicVariation);
        if (data.fieldDirection !== undefined) this.fieldDirection.setImmediate(data.fieldDirection);
        if (data.fieldCellSize !== undefined) this.fieldCellSize.setImmediate(data.fieldCellSize);
        if (data.fieldCellSpacing !== undefined) this.fieldCellSpacing.setImmediate(data.fieldCellSpacing);
        if (data.fieldCustomUpVector !== undefined) this.fieldCustomUpVector.setImmediate(data.fieldCustomUpVector);
        if (data.fieldCustomAlignmentVector !== undefined) this.fieldCustomAlignmentVector.setImmediate(data.fieldCustomAlignmentVector);
        if (data.fieldAlignmentMode !== undefined) this.fieldAlignmentMode.setImmediate(data.fieldAlignmentMode);
        if (data.fieldUpVectorMode !== undefined) this.fieldUpVectorMode.setImmediate(data.fieldUpVectorMode);
        if (data.fieldHoverDirectionOverride !== undefined) this.fieldHoverDirectionOverride.setImmediate(data.fieldHoverDirectionOverride);
        
        // Modern Layouter Properties
        if (data.modernLineFixedSpacing !== undefined) this.modernLineFixedSpacing.setImmediate(data.modernLineFixedSpacing);
        if (data.modernCircleFixedSpacing !== undefined) this.modernCircleFixedSpacing.setImmediate(data.modernCircleFixedSpacing);
        if (data.modernCircleRadius !== undefined) this.modernCircleRadius.setImmediate(data.modernCircleRadius);
        if (data.modernSpiralFixedSpacing !== undefined) this.modernSpiralFixedSpacing.setImmediate(data.modernSpiralFixedSpacing);
        if (data.modernSpiralGrowth !== undefined) this.modernSpiralGrowth.setImmediate(data.modernSpiralGrowth);
        if (data.modernSpiralStartDistance !== undefined) this.modernSpiralStartDistance.setImmediate(data.modernSpiralStartDistance);
        if (data.modernSpiralTurns !== undefined) this.modernSpiralTurns.setImmediate(data.modernSpiralTurns);
        if (data.modernSpiralMaxRadius !== undefined) this.modernSpiralMaxRadius.setImmediate(data.modernSpiralMaxRadius);
        // Modern Surface Layouters
        if (data.modernSphereGridFixedSpacing !== undefined) this.modernSphereGridFixedSpacing.setImmediate(data.modernSphereGridFixedSpacing);
        if (data.modernPlaneGridFixedSpacing !== undefined) this.modernPlaneGridFixedSpacing.setImmediate(data.modernPlaneGridFixedSpacing);
        if (data.modernSphereHexFixedSpacing !== undefined) this.modernSphereHexFixedSpacing.setImmediate(data.modernSphereHexFixedSpacing);
        if (data.modernSphereHexRingsFixedSpacing !== undefined) this.modernSphereHexRingsFixedSpacing.setImmediate(data.modernSphereHexRingsFixedSpacing);
        if (data.modernPlaneHexRingsFixedSpacing !== undefined) this.modernPlaneHexRingsFixedSpacing.setImmediate(data.modernPlaneHexRingsFixedSpacing);
        if (data.modernSphereHexHoneycombFixedSpacing !== undefined) this.modernSphereHexHoneycombFixedSpacing.setImmediate(data.modernSphereHexHoneycombFixedSpacing);
        if (data.modernFlip180 !== undefined) this.modernFlip180.setImmediate(data.modernFlip180);
        
        if (data.layouterProperties) this.layouterProperties = { ...data.layouterProperties };
    }
    
    /**
     * Load layout data from object
     */
    loadFromObject(data: any): void {
        if (data.layouter) this.layouterProperty.value = data.layouter;
        if (data.spacingMode !== undefined) this.spacingMode.setImmediate(data.spacingMode);
        if (data.lineDirection !== undefined) this.lineDirection.setImmediate(data.lineDirection);
        if (data.lineFixedSpacing !== undefined) this.lineFixedSpacing.setImmediate(data.lineFixedSpacing);
        if (data.lineCenterLayout !== undefined) this.lineCenterLayout.setImmediate(data.lineCenterLayout);
        if (data.circleFixedSpacing !== undefined) this.circleFixedSpacing.setImmediate(data.circleFixedSpacing);
        if (data.circleRadius !== undefined) this.circleRadius.setImmediate(data.circleRadius);
        if (data.circleOffsetY !== undefined) this.circleOffsetY.setImmediate(data.circleOffsetY);
        if (data.spiralFixedSpacing !== undefined) this.spiralFixedSpacing.setImmediate(data.spiralFixedSpacing);
        if (data.spiralTurns !== undefined) this.spiralTurns.setImmediate(data.spiralTurns);
        if (data.spiralRadius !== undefined) this.spiralRadius.setImmediate(data.spiralRadius);
        if (data.spiralHeight !== undefined) this.spiralHeight.setImmediate(data.spiralHeight);
        if (data.spiralAxis !== undefined) this.spiralAxis.setImmediate(data.spiralAxis);
        if (data.spiralCenterLayout !== undefined) this.spiralCenterLayout.setImmediate(data.spiralCenterLayout);
        if (data.gridFixedSpacing !== undefined) this.gridFixedSpacing.setImmediate(data.gridFixedSpacing);
        if (data.gridWidth !== undefined) this.gridWidth.setImmediate(data.gridWidth);
        if (data.gridHeight !== undefined) this.gridHeight.setImmediate(data.gridHeight);
        if (data.gridRows !== undefined) this.gridRows.setImmediate(data.gridRows);
        if (data.gridCols !== undefined) this.gridCols.setImmediate(data.gridCols);
        if (data.gridOffsetX !== undefined) this.gridOffsetX.setImmediate(data.gridOffsetX);
        if (data.gridOffsetY !== undefined) this.gridOffsetY.setImmediate(data.gridOffsetY);
        if (data.gridOffsetZ !== undefined) this.gridOffsetZ.setImmediate(data.gridOffsetZ);
        if (data.hexFixedSpacing !== undefined) this.hexFixedSpacing.setImmediate(data.hexFixedSpacing);
        if (data.hexWidth !== undefined) this.hexWidth.setImmediate(data.hexWidth);
        if (data.hexHeight !== undefined) this.hexHeight.setImmediate(data.hexHeight);
        if (data.hexOffsetZ !== undefined) this.hexOffsetZ.setImmediate(data.hexOffsetZ);
        if (data.sphereFixedSpacing !== undefined) this.sphereFixedSpacing.setImmediate(data.sphereFixedSpacing);
        if (data.sphereRadius !== undefined) this.sphereRadius.setImmediate(data.sphereRadius);
        if (data.sphereSubdivisions !== undefined) this.sphereSubdivisions.setImmediate(data.sphereSubdivisions);
        if (data.scatterWidth !== undefined) this.scatterWidth.setImmediate(data.scatterWidth);
        if (data.scatterHeight !== undefined) this.scatterHeight.setImmediate(data.scatterHeight);
        if (data.scatterDepth !== undefined) this.scatterDepth.setImmediate(data.scatterDepth);
        if (data.scatterSeed !== undefined) this.scatterSeed.setImmediate(data.scatterSeed);
        if (data.rotationStep !== undefined) this.rotationStep.setImmediate(data.rotationStep);
        if (data.alignmentMode !== undefined) this.alignmentMode.setImmediate(data.alignmentMode);
        if (data.upVectorMode !== undefined) this.upVectorMode.setImmediate(data.upVectorMode);
        
        // Field Awareness properties
        if (data.fieldAwarenessEnabled !== undefined) this.fieldAwarenessEnabled.setImmediate(data.fieldAwarenessEnabled);
        if (data.resetShapesToDefault !== undefined) this.resetShapesToDefault.setImmediate(data.resetShapesToDefault);
        if (data.fieldStrength !== undefined) this.fieldStrength.setImmediate(data.fieldStrength);
        if (data.fieldRadius !== undefined) this.fieldRadius.setImmediate(data.fieldRadius);
        if (data.fieldFalloff !== undefined) this.fieldFalloff.setImmediate(data.fieldFalloff);
        
        // Extended Field Awareness properties
        if (data.fieldLayoutType !== undefined) this.fieldLayoutType.setImmediate(data.fieldLayoutType);
        if (data.fieldHoverDirection !== undefined) this.fieldHoverDirection.setImmediate(data.fieldHoverDirection);
        if (data.fieldHoverAmplitude !== undefined) this.fieldHoverAmplitude.setImmediate(data.fieldHoverAmplitude);
        if (data.fieldHoverFrequency !== undefined) this.fieldHoverFrequency.setImmediate(data.fieldHoverFrequency);
        if (data.fieldHoverPhaseShift !== undefined) this.fieldHoverPhaseShift.setImmediate(data.fieldHoverPhaseShift);
        if (data.fieldRotateAmplitude !== undefined) this.fieldRotateAmplitude.setImmediate(data.fieldRotateAmplitude);
        if (data.fieldRotateFrequency !== undefined) this.fieldRotateFrequency.setImmediate(data.fieldRotateFrequency);
        if (data.fieldRotatePhaseShift !== undefined) this.fieldRotatePhaseShift.setImmediate(data.fieldRotatePhaseShift);
        if (data.fieldRotationMode !== undefined) this.fieldRotationMode.setImmediate(data.fieldRotationMode);
        if (data.fieldScaleAmplitude !== undefined) this.fieldScaleAmplitude.setImmediate(data.fieldScaleAmplitude);
        if (data.fieldWaveSpeed !== undefined) this.fieldWaveSpeed.setImmediate(data.fieldWaveSpeed);
        if (data.fieldWindStrength !== undefined) this.fieldWindStrength.setImmediate(data.fieldWindStrength);
        if (data.fieldBreathingIntensity !== undefined) this.fieldBreathingIntensity.setImmediate(data.fieldBreathingIntensity);
        if (data.fieldOrganicVariation !== undefined) this.fieldOrganicVariation.setImmediate(data.fieldOrganicVariation);
        if (data.fieldDirection !== undefined) this.fieldDirection.setImmediate(data.fieldDirection);
        if (data.fieldCellSize !== undefined) this.fieldCellSize.setImmediate(data.fieldCellSize);
        if (data.fieldCellSpacing !== undefined) this.fieldCellSpacing.setImmediate(data.fieldCellSpacing);
        if (data.fieldCustomUpVector !== undefined) this.fieldCustomUpVector.setImmediate(data.fieldCustomUpVector);
        if (data.fieldCustomAlignmentVector !== undefined) this.fieldCustomAlignmentVector.setImmediate(data.fieldCustomAlignmentVector);
        if (data.fieldAlignmentMode !== undefined) this.fieldAlignmentMode.setImmediate(data.fieldAlignmentMode);
        if (data.fieldUpVectorMode !== undefined) this.fieldUpVectorMode.setImmediate(data.fieldUpVectorMode);
        if (data.fieldHoverDirectionOverride !== undefined) this.fieldHoverDirectionOverride.setImmediate(data.fieldHoverDirectionOverride);
        
        // Modern Layouter Properties
        if (data.modernLineFixedSpacing !== undefined) this.modernLineFixedSpacing.setImmediate(data.modernLineFixedSpacing);
        if (data.modernCircleFixedSpacing !== undefined) this.modernCircleFixedSpacing.setImmediate(data.modernCircleFixedSpacing);
        if (data.modernCircleRadius !== undefined) this.modernCircleRadius.setImmediate(data.modernCircleRadius);
        if (data.modernSpiralFixedSpacing !== undefined) this.modernSpiralFixedSpacing.setImmediate(data.modernSpiralFixedSpacing);
        if (data.modernSpiralGrowth !== undefined) this.modernSpiralGrowth.setImmediate(data.modernSpiralGrowth);
        if (data.modernSpiralStartDistance !== undefined) this.modernSpiralStartDistance.setImmediate(data.modernSpiralStartDistance);
        if (data.modernSpiralTurns !== undefined) this.modernSpiralTurns.setImmediate(data.modernSpiralTurns);
        if (data.modernSpiralMaxRadius !== undefined) this.modernSpiralMaxRadius.setImmediate(data.modernSpiralMaxRadius);
        // Modern Surface Layouters
        if (data.modernSphereGridFixedSpacing !== undefined) this.modernSphereGridFixedSpacing.setImmediate(data.modernSphereGridFixedSpacing);
        if (data.modernPlaneGridFixedSpacing !== undefined) this.modernPlaneGridFixedSpacing.setImmediate(data.modernPlaneGridFixedSpacing);
        if (data.modernSphereHexFixedSpacing !== undefined) this.modernSphereHexFixedSpacing.setImmediate(data.modernSphereHexFixedSpacing);
        if (data.modernSphereHexRingsFixedSpacing !== undefined) this.modernSphereHexRingsFixedSpacing.setImmediate(data.modernSphereHexRingsFixedSpacing);
        if (data.modernPlaneHexRingsFixedSpacing !== undefined) this.modernPlaneHexRingsFixedSpacing.setImmediate(data.modernPlaneHexRingsFixedSpacing);
        if (data.modernSphereHexHoneycombFixedSpacing !== undefined) this.modernSphereHexHoneycombFixedSpacing.setImmediate(data.modernSphereHexHoneycombFixedSpacing);
        if (data.modernFlip180 !== undefined) this.modernFlip180.setImmediate(data.modernFlip180);
        
        if (data.layouterProperties) this.layouterProperties = { ...data.layouterProperties };
    }

    /**
     * Export layout data to object
     */
    toObject(): any {
        return this.getCurrentValues();
    }

    /**
     * Add layout change callback
     */
    onLayoutChanged(callback: () => void): void {
        // For now, we'll trigger on any property change
        // In a more sophisticated implementation, we'd monitor all InterpolatedProperty changes
        // This is a simplified version
    }

    // Validation
    validate(): boolean {
        // Basic validation
        const layouter = this.layouterProperty.value;
        if (!layouter || typeof layouter !== 'string') {
            return false;
        }
        
        // Grid validation
        if (layouter === 'grid') {
            if ((this.gridFixedSpacing.value ?? 0) <= 0 || (this.gridRows.value ?? 0) <= 0 || (this.gridCols.value ?? 0) <= 0) {
                return false;
            }
        }
        
        // Circle validation
        if (layouter.includes('circle')) {
            if ((this.circleFixedSpacing.value ?? 0) <= 0 || (this.circleRadius.value ?? 0) <= 0) {
                return false;
            }
        }
        
        // Spiral validation
        if (layouter.includes('spiral')) {
            if ((this.spiralFixedSpacing.value ?? 0) <= 0 || (this.spiralRadius.value ?? 0) <= 0 || (this.spiralHeight.value ?? 0) <= 0 || (this.spiralTurns.value ?? 0) <= 0) {
                return false;
            }
        }
        
        // Line validation
        if (layouter.includes('line') || layouter.includes('uphill') || 
            layouter.includes('horizontal') || layouter.includes('vertical')) {
            if ((this.lineFixedSpacing.value ?? 0) <= 0) {
                return false;
            }
        }
        
        // Hexagonal validation
        if (layouter.includes('hexagonal')) {
            if ((this.hexFixedSpacing.value ?? 0) < 0) {
                return false;
            }
        }
        
        // Spherical validation  
        if (layouter.includes('spherical')) {
            if ((this.sphereFixedSpacing.value ?? 0) <= 0 || (this.sphereRadius.value ?? 0) <= 0) {
                return false;
            }
        }
        
        return true;
    }
    
    // Serialization - only store target values
    serialize(): any {
        return this.getCurrentValues();
    }
    
    // Deserialization
    deserialize(data: any): LayoutData {
        this.setImmediate(data);
        return this;
    }
    
    // Factory method
    static create(data: any = {}): LayoutData {
        const layoutData = new LayoutData(data.layouter);
        if (data && Object.keys(data).length > 1) {
            layoutData.setImmediate(data);
        }
        return layoutData;
    }
    
    // Clone
    clone(): LayoutData {
        return LayoutData.create(this.serialize());
    }
} 